import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  computed,
  signal,
  effect,
  OnDestroy,
  DestroyRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Copy, Check, Loader2, AlertCircle, RefreshCw } from 'lucide-angular';
import { MarkdownModule } from 'ngx-markdown';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChatMessage,
  ChatMessageRole,
  ChatMessageStatus,
  ToolCallStatus,
  MaterialsAnalysisPreview,
  ScoringGuidePreview,
  GradingResultsPreview,
  StandardToolOutput,
} from '../../grading.types';
import { GradingApiService } from '../../services/grading-api.service';
import { MessageAttachmentsPreviewComponent } from '../message-attachments-preview/message-attachments-preview.component';
import { MaterialsAnalysisPreviewComponent } from '../materials-analysis-preview/materials-analysis-preview.component';
import { ScoringGuidePreviewComponent } from '../scoring-guide-preview/scoring-guide-preview.component';
import { GradingResultsPreviewComponent } from '../grading-results-preview/grading-results-preview.component';

interface EntityFetchState<T> {
  loading: boolean;
  data: T | null;
  error: string | null;
}

@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    MarkdownModule,
    MessageAttachmentsPreviewComponent,
    MaterialsAnalysisPreviewComponent,
    ScoringGuidePreviewComponent,
    GradingResultsPreviewComponent,
  ],
  templateUrl: './chat-message.component.html',
  styleUrl: './chat-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly gradingApi = inject(GradingApiService);
  readonly message = input.required<ChatMessage>();

  /** Emitted when async content (tool previews) finishes loading */
  readonly contentLoaded = output<void>();

  protected readonly icons = {
    Copy,
    Check,
    Loader2,
    AlertCircle,
    RefreshCw,
  };

  protected readonly ChatMessageRole = ChatMessageRole;
  protected readonly ChatMessageStatus = ChatMessageStatus;
  protected readonly ToolCallStatus = ToolCallStatus;
  protected readonly copied = signal(false);

  // Entity fetch states
  protected readonly materialsAnalysisState = signal<EntityFetchState<MaterialsAnalysisPreview>>({
    loading: false,
    data: null,
    error: null,
  });

  protected readonly scoringGuideState = signal<EntityFetchState<ScoringGuidePreview>>({
    loading: false,
    data: null,
    error: null,
  });

  protected readonly gradingRunState = signal<EntityFetchState<GradingResultsPreview>>({
    loading: false,
    data: null,
    error: null,
  });

  // Timer state
  private timerIntervalId: ReturnType<typeof setInterval> | null = null;
  private timerStartTime: number | null = null;
  protected readonly elapsedSeconds = signal(0);

  // Track which entities we've already fetched to avoid re-fetching
  private fetchedEntities = new Set<string>();

  /**
   * Check if the message is in loading state
   */
  protected readonly isLoading = computed(
    () => this.message().status === ChatMessageStatus.LOADING,
  );

  /**
   * Check if this is an AI message
   */
  protected readonly isAiMessage = computed(
    () => this.message().role === ChatMessageRole.ASSISTANT,
  );

  /**
   * Get the names of tools currently being executed
   */
  protected readonly activeToolNames = computed(() => {
    const msg = this.message();
    if (!msg.toolCalls || msg.toolCalls.length === 0) return [];
    return msg.toolCalls
      .filter((tc) => tc.status === ToolCallStatus.PENDING)
      .map((tc) => this.formatToolName(tc.name));
  });

  /**
   * Get entity IDs from completed tool calls
   */
  private readonly completedToolEntities = computed(() => {
    const msg = this.message();
    if (!msg.toolCalls || msg.toolCalls.length === 0) return null;

    const entities: {
      materialsAnalysis?: { gradingId: string };
      scoringGuide?: { scoringGuideId: string };
      gradingRun?: { gradingRunId: string };
    } = {};

    for (const tc of msg.toolCalls) {
      if (tc.status !== ToolCallStatus.SUCCESS || !tc.result?.raw) continue;

      const result = tc.result.raw as StandardToolOutput;
      if (!result.entities) continue;

      switch (tc.name) {
        case 'update_materials_analysis':
          if (result.entities['gradingId']) {
            entities.materialsAnalysis = { gradingId: result.entities['gradingId'] };
          }
          break;
        case 'generate_scoring_guide':
          if (result.entities['scoringGuideId']) {
            entities.scoringGuide = { scoringGuideId: result.entities['scoringGuideId'] };
          }
          break;
        case 'grade_student_works':
          if (result.entities['gradingRunId']) {
            entities.gradingRun = { gradingRunId: result.entities['gradingRunId'] };
          }
          break;
      }
    }

    return entities;
  });

  /**
   * Materials analysis preview (from fetched data)
   */
  protected readonly materialsAnalysisPreview = computed(() => this.materialsAnalysisState().data);

  /**
   * Scoring guide preview (from fetched data)
   */
  protected readonly scoringGuidePreview = computed(() => this.scoringGuideState().data);

  /**
   * Grading results preview (from fetched data)
   */
  protected readonly gradingResultsPreview = computed(() => this.gradingRunState().data);

  /**
   * Format the elapsed time for live timer display
   */
  protected readonly liveTimerDisplay = computed(() => {
    const seconds = this.elapsedSeconds();
    return this.formatDuration(seconds * 1000);
  });

  /**
   * Format the final generation duration
   */
  protected readonly generationDurationDisplay = computed(() => {
    const msg = this.message();
    if (msg.generationDurationMs === undefined) return null;
    return this.formatDuration(msg.generationDurationMs);
  });

  /**
   * Process markdown content to wrap tables in a scrollable container.
   * This allows tables to scroll horizontally without affecting the page.
   */
  protected readonly processedContent = computed(() => {
    const content = this.message().content;
    if (!content) return '';

    // Regex to match markdown tables (lines starting with |)
    // A table consists of: header row, separator row (with dashes), and data rows
    const tableRegex = /(\|[^\n]+\|\n\|[-:\s|]+\|\n(?:\|[^\n]+\|\n?)+)/g;

    return content.replace(tableRegex, (table) => {
      return `<div class="table-scroll-wrapper">\n\n${table}\n\n</div>`;
    });
  });

  constructor() {
    // Effect to start/stop timer based on loading state
    effect(() => {
      const loading = this.isLoading();
      const isAi = this.isAiMessage();

      if (loading && isAi) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    });

    // Effect to fetch entities when tool calls complete
    effect(() => {
      const entities = this.completedToolEntities();
      if (!entities) return;

      // Fetch materials analysis
      if (entities.materialsAnalysis) {
        const key = `materials:${entities.materialsAnalysis.gradingId}`;
        if (!this.fetchedEntities.has(key)) {
          this.fetchedEntities.add(key);
          this.fetchMaterialsAnalysis(entities.materialsAnalysis.gradingId);
        }
      }

      // Fetch scoring guide
      if (entities.scoringGuide) {
        const key = `scoring:${entities.scoringGuide.scoringGuideId}`;
        if (!this.fetchedEntities.has(key)) {
          this.fetchedEntities.add(key);
          this.fetchScoringGuide(entities.scoringGuide.scoringGuideId);
        }
      }

      // Fetch grading run
      if (entities.gradingRun) {
        const key = `run:${entities.gradingRun.gradingRunId}`;
        if (!this.fetchedEntities.has(key)) {
          this.fetchedEntities.add(key);
          this.fetchGradingRun(entities.gradingRun.gradingRunId);
        }
      }
    });

    // Cleanup on destroy
    this.destroyRef.onDestroy(() => {
      this.stopTimer();
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private fetchMaterialsAnalysis(gradingId: string): void {
    this.materialsAnalysisState.set({ loading: true, data: null, error: null });

    this.gradingApi.getMaterialsAnalysisPreview(gradingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.materialsAnalysisState.set({ loading: false, data, error: null });
          // Emit after a microtask to allow DOM to update
          setTimeout(() => this.contentLoaded.emit());
        },
        error: (err) => {
          this.materialsAnalysisState.set({
            loading: false,
            data: null,
            error: err.message || 'Failed to load materials analysis',
          });
        },
      });
  }

  private fetchScoringGuide(scoringGuideId: string): void {
    this.scoringGuideState.set({ loading: true, data: null, error: null });

    this.gradingApi.getScoringGuide(scoringGuideId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.scoringGuideState.set({ loading: false, data, error: null });
          // Emit after a microtask to allow DOM to update
          setTimeout(() => this.contentLoaded.emit());
        },
        error: (err) => {
          this.scoringGuideState.set({
            loading: false,
            data: null,
            error: err.message || 'Failed to load scoring guide',
          });
        },
      });
  }

  private fetchGradingRun(gradingRunId: string): void {
    this.gradingRunState.set({ loading: true, data: null, error: null });

    this.gradingApi.getGradingRun(gradingRunId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          // Transform GradingRun to GradingResultsPreview
          const preview: GradingResultsPreview = {
            gradingId: run.gradingId,
            gradingRunId: run.id,
            studentCount: run.stats.totalStudents,
            completedCount: run.stats.totalSuccessful,
            failedCount: run.stats.totalFailed,
            averagePercentage: run.stats.averagePercentage,
            minPercentage: run.stats.minPercentage,
            maxPercentage: run.stats.maxPercentage,
            gradeDistribution: run.stats.gradeDistribution,
            students: run.students,
            sectionStats: run.sectionStats,
            aiAnalysisGenerated: run.aiAnalysisGenerated,
            questionAnalysis: run.questionAnalysis,
          };
          this.gradingRunState.set({ loading: false, data: preview, error: null });
          // Emit after a microtask to allow DOM to update
          setTimeout(() => this.contentLoaded.emit());
        },
        error: (err) => {
          this.gradingRunState.set({
            loading: false,
            data: null,
            error: err.message || 'Failed to load grading results',
          });
        },
      });
  }

  protected retryFetch(type: 'materials' | 'scoring' | 'grading'): void {
    const entities = this.completedToolEntities();
    if (!entities) return;

    switch (type) {
      case 'materials':
        if (entities.materialsAnalysis) {
          this.fetchedEntities.delete(`materials:${entities.materialsAnalysis.gradingId}`);
          this.fetchMaterialsAnalysis(entities.materialsAnalysis.gradingId);
        }
        break;
      case 'scoring':
        if (entities.scoringGuide) {
          this.fetchedEntities.delete(`scoring:${entities.scoringGuide.scoringGuideId}`);
          this.fetchScoringGuide(entities.scoringGuide.scoringGuideId);
        }
        break;
      case 'grading':
        if (entities.gradingRun) {
          this.fetchedEntities.delete(`run:${entities.gradingRun.gradingRunId}`);
          this.fetchGradingRun(entities.gradingRun.gradingRunId);
        }
        break;
    }
  }

  private startTimer(): void {
    if (this.timerIntervalId !== null) return;

    this.timerStartTime = Date.now();
    this.elapsedSeconds.set(0);

    this.timerIntervalId = setInterval(() => {
      if (this.timerStartTime !== null) {
        const elapsed = Math.floor((Date.now() - this.timerStartTime) / 1000);
        this.elapsedSeconds.set(elapsed);
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerIntervalId !== null) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
    this.timerStartTime = null;
  }

  private formatDuration(ms: number): string {
    const totalSeconds = Math.round(ms / 1000);

    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}m`;
  }

  private formatToolName(name: string): string {
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  protected formatTime(date: Date): string {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  protected copyMessage(): void {
    const text = this.message().content;

    // Try modern clipboard API first, with fallback for iOS Safari
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text)
        .then(() => this.onCopySuccess())
        .catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string): void {
    // Fallback for iOS Safari and older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    textarea.setAttribute('readonly', ''); // Prevent keyboard on mobile
    document.body.appendChild(textarea);

    // iOS Safari requires special handling
    const range = document.createRange();
    range.selectNodeContents(textarea);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    textarea.setSelectionRange(0, text.length); // For iOS

    try {
      document.execCommand('copy');
      this.onCopySuccess();
    } catch (err) {
      console.error('Failed to copy:', err);
    }

    document.body.removeChild(textarea);
  }

  private onCopySuccess(): void {
    this.copied.set(true);
    setTimeout(() => {
      this.copied.set(false);
    }, 2000);
  }
}
